<script lang="ts" setup>
import { computed, onBeforeUnmount, ref, watch, useAttrs, type VNode } from 'vue';
import { get } from 'lodash-es';
import { IconCopy, IconFilterPlus, IconSparkles } from '@/icons';
import { injectStrict } from '../../utils';
import { ObActionList, ObActionListContextProvider, ObActionListItem } from '../action-list';
import { ObHostedDropdown } from '../hosted-dropdown';
import { useToaster } from '../../services/toaster';
import { CELL_CONTEXT, useTableContext } from './shared';
interface Props {
  align?: 'start' | 'end' | 'center';
  monospace?: boolean;
  clickable?: boolean;
  active?: boolean;
  padding?: 'default' | 'compact';
}
defineOptions({ inheritAttrs: false });
const props = defineProps<Props>();
defineSlots<{
  default?: (props: { value: string; rawValue: any }) => VNode;
}>();
const { align, monospace, value, rawValue, columnKey, filterKey, filterValueGetter, row } =
  injectStrict(CELL_CONTEXT);
const tableContext = useTableContext();
const attrs = useAttrs();
const contextMenuOpen = ref(false);
const contextMenuPosition = ref({ x: 0, y: 0 });
const cellRef = ref<HTMLElement | null>(null);
const menuRef = ref<HTMLElement | null>(null);
const toaster = useToaster();
const contextMenuKey = computed(() => {
  if (!row || !columnKey) {
    return null;
  }
  return `${tableContext.getRowKey(row)}:${columnKey}`;
});
const resolvedFilterKey = computed(() => {
  if (!tableContext.filterByValueEnabled.value) {
    return null;
  }
  if (filterKey) {
    return filterKey;
  }
  if (columnKey && tableContext.filterableColumnKeys.value.has(columnKey)) {
    return columnKey;
  }
  return null;
});
const canFilter = computed(() => !!resolvedFilterKey.value);
const resolvedCellValue = computed(() => {
  if (rawValue == null) {
    return value;
  }
  if (typeof rawValue === 'object') {
    return value;
  }
  return rawValue;
});
const resolvedFilterValue = computed(() => {
  if (!resolvedFilterKey.value || !row) {
    return resolvedCellValue.value;
  }
  if (filterValueGetter) {
    return filterValueGetter(row);
  }
  const candidate = get(row, resolvedFilterKey.value);
  return candidate === undefined ? resolvedCellValue.value : candidate;
});
const copyValue = computed(() => {
  const resolvedValue = resolvedCellValue.value;
  return resolvedValue == null ? '' : String(resolvedValue);
});
function isMeaningfulValue(value: unknown): boolean {
  if (value == null) {
    return false;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return false;
    }
    return !['-', '—', '–'].includes(trimmed);
  }
  if (typeof value === 'number') {
    return true;
  }
  if (typeof value === 'boolean') {
    return true;
  }
  if (value instanceof Date) {
    return true;
  }
  if (Array.isArray(value)) {
    return value.some((item) => isMeaningfulValue(item));
  }
  if (typeof value === 'object') {
    return Object.values(value).some((item) => isMeaningfulValue(item));
  }
  return true;
}
const hasCellValue = computed(() => {
  const displayValue = value ?? '';
  const trimmedValue = displayValue.trim();
  if (
    trimmedValue &&
    trimmedValue !== '[object Object]' &&
    !['-', '—', '–'].includes(trimmedValue)
  ) {
    return true;
  }
  return isMeaningfulValue(resolvedCellValue.value);
});
function onContextMenu(event: MouseEvent) {
  event.preventDefault();
  if (!contextMenuKey.value || !cellRef.value) {
    return;
  }
  if (!hasCellValue.value) {
    return;
  }
  if (tableContext.activeContextMenuKey.value === contextMenuKey.value) {
    tableContext.setActiveContextMenuKey(null);
    cellRef.value.blur();
    return;
  }
  const rect = cellRef.value.getBoundingClientRect();
  contextMenuPosition.value = {
    x: rect.left + 8,
    y: rect.bottom - 2,
  };
  tableContext.setActiveContextMenuKey(contextMenuKey.value);
}
async function copyToClipboard() {
  const text = copyValue.value;
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    toaster.create('Copied to clipboard', {
      variant: 'success',
      duration: 2000,
      key: 'data-table-copy',
    });
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  textarea.style.top = '-9999px';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
  toaster.create('Copied to clipboard', {
    variant: 'success',
    duration: 2000,
    key: 'data-table-copy',
  });
}
function filterByValue() {
  if (!columnKey || !resolvedFilterKey.value) {
    return;
  }
  tableContext.filterByValue(columnKey, resolvedFilterKey.value, resolvedFilterValue.value);
}
function askAiByValue() {
  if (!row || !columnKey) {
    return;
  }
  tableContext.askAiByValue({
    row,
    columnKey,
    filterKey: resolvedFilterKey.value ?? undefined,
    value: resolvedFilterValue.value,
    rawValue: resolvedCellValue.value,
    displayValue: value,
  });
}
const contextHostStyle = computed(() => ({
  left: `${contextMenuPosition.value.x}px`,
  top: `${contextMenuPosition.value.y}px`,
}));
function closeContextMenu() {
  if (!contextMenuOpen.value) {
    return;
  }
  tableContext.setActiveContextMenuKey(null);
}
function onOutsideContextMenu(event: MouseEvent) {
  const target = event.target as Node | null;
  if (menuRef.value?.contains?.(target) || cellRef.value?.contains(target)) {
    return;
  }
  closeContextMenu();
}
watch(
  () => tableContext.activeContextMenuKey.value,
  (value) => {
    contextMenuOpen.value = value !== null && value === contextMenuKey.value;
  },
  { immediate: true },
);
watch(contextMenuOpen, (value) => {
  if (value) {
    window.addEventListener('contextmenu', onOutsideContextMenu, true);
    window.addEventListener('scroll', closeContextMenu, true);
    return;
  }
  window.removeEventListener('contextmenu', onOutsideContextMenu, true);
  window.removeEventListener('scroll', closeContextMenu, true);
});
onBeforeUnmount(() => {
  window.removeEventListener('contextmenu', onOutsideContextMenu, true);
  window.removeEventListener('scroll', closeContextMenu, true);
});
</script>

<template>
  <div
    ref="cellRef"
    :role="props.clickable ? 'button' : undefined"
    :tabindex="props.clickable ? 0 : undefined"
    v-bind="attrs"
    :class="[
      $style.root,
      {
        [$style.clickable]: props.clickable,
        [$style.alignEnd]: align === 'end',
        [$style.alignCenter]: align === 'center',
        [$style.monospace]: monospace,
        [$style.active]: props.active,
        [$style.paddingCompact]: props.padding === 'compact',
        [$style.contextActive]: contextMenuOpen,
      },
    ]"
    @contextmenu="onContextMenu"
  >
    <slot v-bind="{ value, rawValue }" />
    <ObHostedDropdown
      v-if="contextMenuOpen && hasCellValue"
      v-model:active="contextMenuOpen"
      shift
      flip
      :padding="5"
      @click-outside="closeContextMenu"
    >
      <template #host="{ hostProps }">
        <Teleport to="body">
          <button
            v-bind="hostProps"
            type="button"
            :class="$style.contextHost"
            :style="contextHostStyle"
            aria-hidden="true"
            tabindex="-1"
          />
        </Teleport>
      </template>
      <div ref="menuRef">
        <ObActionListContextProvider :on-after-select="closeContextMenu">
          <ObActionList>
            <ObActionListItem v-if="canFilter" @select="filterByValue">
              <template #leadingVisual>
                <IconFilterPlus />
              </template>
              Filter by value
            </ObActionListItem>
            <ObActionListItem @select="copyToClipboard">
              <template #leadingVisual>
                <IconCopy />
              </template>
              Copy value
            </ObActionListItem>
            <ObActionListItem
              v-if="tableContext.askAiByValueEnabled.value"
              :class="$style.askAiAction"
              @select="askAiByValue"
            >
              <template #leadingVisual>
                <IconSparkles />
              </template>
              {{ tableContext.resolveI18n('askAi') }}
            </ObActionListItem>
          </ObActionList>
        </ObActionListContextProvider>
      </div>
    </ObHostedDropdown>
  </div>
</template>

<style lang="scss" module>
@use '../../styles/shared';
@use '../../styles/colors';
@use '../../styles/typography';
@use './shared' as tableSharedStyles;

.root {
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex: none;
  padding: 12px;
  width: 100%;
  align-items: center;
  font-family: typography.$font-family-primary;
  font-size: 13px;
  line-height: 16px;
  min-height: 40px;
  overflow: hidden;
  outline: none;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    border: 2px solid transparent;
    box-sizing: border-box;
    z-index: tableSharedStyles.$z-index-border + 1;
  }
}

.paddingCompact {
  padding: 4px;
}

.contextHost {
  position: fixed;
  width: 1px;
  height: 1px;
  opacity: 0;
  border: 0;
  padding: 0;
  pointer-events: none;
}

.clickable {
  cursor: pointer;
  user-select: none;
  z-index: tableSharedStyles.$z-index-clickable-cel;

  &.active,
  &:active {
    background: rgba(#23395d, 0.05);
  }
}

.contextActive {
  &::after {
    border-color: var(--ui2-background-accent-primary, #226b6c);
  }
}

.root:focus::after,
.root:focus-visible::after,
.contextActive::after {
  border-color: var(--ui2-background-accent-primary, #226b6c);
}

.content {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.alignCenter {
  justify-content: center;
}

.askAiAction {
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid transparent;
  background: var(--ui2-background-primary, #fff);
  z-index: 0;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 300%;
    aspect-ratio: 1;
    transform-origin: center;
    translate: -50% -50%;
    border-radius: 50%;
    background: conic-gradient(
      from 0deg,
      var(--ui2-stroke-tertiary, #f0f1f4) 0deg,
      var(--ui2-primitive-pine-forest-500, rgba(34, 107, 108, 1)) 45deg,
      var(--ui2-stroke-tertiary, #f0f1f4) 90deg,
      var(--ui2-stroke-tertiary, #f0f1f4) 360deg
    );
    animation: spinBorder 1.5s linear infinite;
    z-index: -2;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 1.4px;
    border-radius: 6px;
    background: var(--ui2-background-primary, #fff);
    z-index: -1;
  }

  > button {
    border-radius: 6px;
    color: var(--ui2-background-accent-primary, #226b6c);
    background: transparent;
  }

  > button svg,
  > button svg path {
    fill: currentColor !important;
  }

  > button:hover:not(:disabled),
  > button[data-is-active-descendant] {
    background:
      linear-gradient(0deg, rgba(34, 107, 108, 0.08) 0%, rgba(34, 107, 108, 0.08) 100%),
      var(--ui2-background-primary, #fff);
  }
}

@keyframes spinBorder {
  to {
    transform: rotate(360deg);
  }
}

.alignEnd {
  justify-content: flex-end;
}

.monospace {
  font-family: typography.$font-family-monospace;
}
</style>
